type DateTimePickerTextFieldProps = Record<string, unknown>

type DateTimePickerStylesOptions = {
  isMobile?: boolean
  /** TimePicker toolbar uses huge h3 digits; hide it to match DateTimePicker lists. */
  hideToolbar?: boolean
  textField?: DateTimePickerTextFieldProps
}

const popperSx = {
  '& .MuiPaper-root': {
    background: 'var(--glass-bg) !important',
    backdropFilter: 'var(--glass-blur) !important',
    WebkitBackdropFilter: 'var(--glass-blur) !important',
    borderRadius: 'var(--radius-xl) !important',
    boxShadow: 'var(--glass-shadow) !important',
    border: 'var(--form-border) !important',
    overflow: 'hidden !important',
    padding: '8px 8px 0 !important',
  },
}

const layoutSx = {
  '& .MuiPaper-root, & .MuiPickersLayout-root': {
    background: 'transparent !important',
    color: 'var(--text-main) !important',
    borderRadius: 'var(--radius-xl) !important',
    fontFamily: 'var(--font-main)',
  },
  '& .MuiTypography-root, & .MuiPickersDay-root, & .MuiButtonBase-root': {
    fontFamily: 'var(--font-main)',
    color: 'var(--text-main) !important',
  },
  '& .MuiPickersDay-today': {
    border: '1px solid var(--color-info) !important',
  },
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: 'var(--color-info) !important',
    color: 'white !important',
    borderRadius: 'var(--radius-lg) !important',
  },
  '& .MuiPickersDay-root:hover': {
    backgroundColor: 'var(--glass-bg-hover) !important',
  },
  '& .MuiMultiSectionDigitalClock-root': {
    paddingTop: '4px',
  },
  '& .MuiMultiSectionDigitalClockSection-root': {
    paddingTop: '4px',
  },
  '& .MuiMultiSectionDigitalClockSection-item': {
    borderRadius: 'var(--radius-lg) !important',
    margin: '2px 4px !important',
    color: 'var(--text-main) !important',
  },
  '& .MuiMultiSectionDigitalClockSection-item:hover': {
    backgroundColor: 'var(--glass-bg-hover) !important',
  },
  '& .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
    backgroundColor: 'var(--color-info) !important',
    color: 'white !important',
    borderRadius: 'var(--radius-lg) !important',
  },
  '& .MuiPickersToolbar-root': {
    backgroundColor: 'var(--glass-bg-dark) !important',
  },
  '& .MuiTimePickerToolbar-hourMinuteLabel, & .MuiTimePickerToolbar-separator': {
    fontSize: '1.25rem !important',
    lineHeight: 1.2,
  },
}

const textFieldSx = {
  '& .MuiInputBase-root': {
    height: 36,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-lg)',
    backgroundColor: 'var(--glass-bg)',
    backdropFilter: 'var(--glass-blur)',
    color: 'var(--text-main)',
    fontFamily: 'var(--font-main)',
    '& fieldset': {
      border: 'var(--form-border) !important',
      borderRadius: 'var(--radius-lg)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--color-info) !important',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    fontFamily: 'var(--font-main)',
    color: 'var(--text-secondary)',
    top: '-20%',
  },
  '& .MuiInputLabel-shrink': {
    top: '-20%',
    left: '12px',
    transform: 'none',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--text-secondary)',
  },
}

/** Shared glass style for MUI DateTimePicker and TimePicker fields. */
export const dateTimePickerStyles = ({
  isMobile = false,
  hideToolbar = isMobile,
  textField,
}: DateTimePickerStylesOptions = {}) => ({
  popper: {
    sx: popperSx,
  },
  toolbar: {
    hidden: hideToolbar,
    sx: {
      '& .MuiDateTimePickerToolbar-dateContainer .MuiTypography-root': {
        fontSize: isMobile ? '1rem !important' : 'inherit',
      },
      '& .MuiDateTimePickerToolbar-timeContainer .MuiTypography-root': {
        fontSize: isMobile ? '1rem !important' : 'inherit',
      },
    },
  },
  layout: {
    sx: layoutSx,
  },
  textField: {
    ...textField,
    sx: textFieldSx,
  },
})
